import type { ReactNode } from 'react';
import { FONDS_DE_PAPIER, type FondDePapier } from '@nextilt/commun';
import type { ChangementDePapier, Papier } from '../partage/api/types';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import { CHANGER_DE_PAPIER, MOTS_DES_FONDS } from '../partage/vocabulaire';
import { PoigneeDuPapier } from './PoigneeDuPapier';
import { decorDuPapier } from './reglesDesPapiers';
import { useGlisserUnPapier, type CarteDesPapiers } from './useGlisserUnPapier';

/**
 * Ce que tout papier posé sur la carte porte autour de son contenu (chantier post-it-et-listes,
 * tickets 02 à 04), écrit une fois pour le post-it et la liste libre : sa place gardée (son
 * centre) et sa taille, le glisser et la poignée (`useGlisserUnPapier`, ticket 03), le scotch ou
 * la punaise, et ses deux petits boutons, son papier et la croix.
 *
 * Il grandit si son contenu dépasse la hauteur gardée ; un appui dedans ne fait jamais glisser le
 * regard (`cameraCarte.ts`).
 */
export function CadreDuPapier({
  papier,
  carte,
  supprimer,
  onPlacer,
  onChangerDeFond,
  onSupprimer,
  children,
}: {
  papier: Papier;
  carte: CarteDesPapiers;
  /** Ce que dit la croix : « Supprimer ce post-it », « Supprimer cette liste ». */
  supprimer: string;
  /** La place ou la taille, au lâcher ; la promesse est tenue quand le serveur a répondu. */
  onPlacer: (id: string, changement: ChangementDePapier) => Promise<void>;
  onChangerDeFond: (id: string, fond: FondDePapier) => void;
  onSupprimer: (id: string) => void;
  children: ReactNode;
}) {
  const { attache, penche } = decorDuPapier(papier.id);
  const glisser = useGlisserUnPapier(papier, carte, onPlacer);
  const { cadre } = glisser;
  const menu = useMenuOuvert();
  const sorte = papier.sorte === 'post_it' ? 'post-it' : 'liste';

  return (
    <div
      ref={glisser.element}
      className={`papier papier--${sorte} papier--${papier.fond}${glisser.geste ? ` papier--${glisser.geste}` : ''}`}
      data-papier={papier.id}
      style={{
        left: cadre.x,
        top: cadre.y,
        width: cadre.largeur,
        minHeight: cadre.hauteur,
        ['--penche' as string]: `${penche}deg`,
      }}
      onPointerDown={glisser.surLePapier}
    >
      <span className={`papier-attache papier-attache--${attache}`} aria-hidden="true" />
      {children}

      <div className={`papier-outils${menu.ouvert ? ' est-ouvert' : ''}`} ref={menu.ref}>
        <button
          type="button"
          className={`papier-outil papier-pastille papier-pastille--${papier.fond}`}
          aria-label={CHANGER_DE_PAPIER}
          aria-expanded={menu.ouvert}
          title={CHANGER_DE_PAPIER}
          onClick={() => menu.setOuvert(!menu.ouvert)}
        />
        <button
          type="button"
          className="papier-outil papier-croix"
          aria-label={supprimer}
          title={supprimer}
          onClick={() => onSupprimer(papier.id)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 7l10 10M17 7 7 17" />
          </svg>
        </button>
        {menu.ouvert && (
          <div className="papier-fonds" role="menu" aria-label={CHANGER_DE_PAPIER}>
            {FONDS_DE_PAPIER.map((fond) => (
              <button
                key={fond}
                type="button"
                role="menuitemradio"
                aria-checked={fond === papier.fond}
                className={`papier-pastille papier-pastille--${fond}`}
                data-fond={fond}
                title={MOTS_DES_FONDS[fond]}
                aria-label={MOTS_DES_FONDS[fond]}
                onClick={() => {
                  menu.setOuvert(false);
                  if (fond !== papier.fond) onChangerDeFond(papier.id, fond);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <PoigneeDuPapier onPointerDown={glisser.surLaPoignee} />
    </div>
  );
}
